// src/components/CourseCreator.js - Create Custom Courses
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Plus, Trash2, BookOpen, Target, Save, ArrowLeft,
    AlertCircle, CheckCircle, X, Terminal
} from 'lucide-react';
import { apiService, handleApiError } from '../services/api';
import Loading from './common/Loading';

export default function CourseCreator() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showQuickPrompt, setShowQuickPrompt] = useState(false);
    const [quickPrompt, setQuickPrompt] = useState('');
    const [generating, setGenerating] = useState(false);

    // If we arrived from the tutor agent's "Create this course" CTA,
    // it stashes the proposal on location.state. Pre-fill the AI quick-
    // prompt with what the agent already figured out so the user keeps
    // their context. They click Generate to proceed (we don't auto-fire
    // — leave them in control to tweak first).
    useEffect(() => {
        const p = location.state?.proposal;
        if (!p) return;
        const prompt = [p.name, p.description, p.why_this]
            .filter(Boolean)
            .join('. ');
        setQuickPrompt(prompt);
        setShowQuickPrompt(true);
    }, [location.state]);

    // Course form state
    const [courseData, setCourseData] = useState({
        name: '',
        code: '',
        description: '',
        domains: [
            {
                key: '',
                name: '',
                weight: 20,
                concepts: [''],
                description: '',
                sandbox_eligible: false
            }
        ]
    });

    const addDomain = () => {
        setCourseData(prev => ({
            ...prev,
            domains: [...prev.domains, {
                key: '',
                name: '',
                weight: 20,
                concepts: [''],
                description: '',
                sandbox_eligible: false
            }]
        }));
    };

    const removeDomain = (index) => {
        if (courseData.domains.length > 1) {
            setCourseData(prev => ({
                ...prev,
                domains: prev.domains.filter((_, i) => i !== index)
            }));
        }
    };

    const updateDomain = (index, field, value) => {
        setCourseData(prev => ({
            ...prev,
            domains: prev.domains.map((domain, i) =>
                i === index ? { ...domain, [field]: value } : domain
            )
        }));
    };

    const addConcept = (domainIndex) => {
        setCourseData(prev => ({
            ...prev,
            domains: prev.domains.map((domain, i) =>
                i === domainIndex
                    ? { ...domain, concepts: [...domain.concepts, ''] }
                    : domain
            )
        }));
    };

    const removeConcept = (domainIndex, conceptIndex) => {
        setCourseData(prev => ({
            ...prev,
            domains: prev.domains.map((domain, i) =>
                i === domainIndex
                    ? { ...domain, concepts: domain.concepts.filter((_, ci) => ci !== conceptIndex) }
                    : domain
            )
        }));
    };

    const updateConcept = (domainIndex, conceptIndex, value) => {
        setCourseData(prev => ({
            ...prev,
            domains: prev.domains.map((domain, i) =>
                i === domainIndex
                    ? {
                        ...domain,
                        concepts: domain.concepts.map((concept, ci) =>
                            ci === conceptIndex ? value : concept
                        )
                    }
                    : domain
            )
        }));
    };

    // Auto-generate course code from name
    const handleNameChange = (value) => {
        setCourseData(prev => ({
            ...prev,
            name: value,
            code: prev.code || value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6)
        }));
    };

    // Auto-generate domain key from name
    const handleDomainNameChange = (index, value) => {
        const key = value.toLowerCase().replace(/[^a-z0-9]/g, '_');
        setCourseData(prev => ({
            ...prev,
            domains: prev.domains.map((domain, i) =>
                i === index ? { ...domain, name: value, key: key } : domain
            )
        }));
    };

    // AI Course Generation Handler
    const handleGenerateCourse = async () => {
        if (!quickPrompt.trim()) return;
        
        setGenerating(true);
        setError(null);
        
        try {
            const response = await apiService.generateCourseStructure(quickPrompt);
            
            // Pre-fill the form with generated data
            setCourseData(response.data.course_data);
            setShowQuickPrompt(false);
            setQuickPrompt('');
            
            // Show success feedback
            setError(null);
            // You could add a success toast here instead
            
        } catch (error) {
            setError(handleApiError(error));
        } finally {
            setGenerating(false);
        }
    };

    const validateForm = () => {
        if (!courseData.name.trim()) return "Course name is required";
        if (!courseData.code.trim()) return "Course code is required";
        if (courseData.domains.length === 0) return "At least one domain is required";

        // Domain weights — server normalizes them to sum to 1.0 even if the
        // form total is off, so we just need at least one positive weight.
        const totalWeight = courseData.domains.reduce((sum, domain) => sum + domain.weight, 0);
        if (totalWeight <= 0) return "Domain weights must be positive";

        // Validate domains
        for (let i = 0; i < courseData.domains.length; i++) {
            const domain = courseData.domains[i];
            if (!domain.name.trim()) return `Domain ${i + 1} name is required`;
            if (!domain.key.trim()) return `Domain ${i + 1} key is required`;
            if (domain.concepts.filter(c => c.trim()).length === 0) {
                return `Domain ${i + 1} must have at least one concept`;
            }
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Clean up the data
            const cleanedData = {
                ...courseData,
                domains: courseData.domains.map(domain => ({
                    ...domain,
                    concepts: domain.concepts.filter(c => c.trim()).map(c => c.trim()),
                    weight: domain.weight / 100 // Convert percentage to decimal
                }))
            };

            const response = await apiService.createCourse(cleanedData);

            setSuccess(true);
            setTimeout(() => {
                // Redirect to Daily Practice for immediate onboarding
                navigate(`/course/${response.data.code}/practice`);
            }, 2000);

        } catch (error) {
            setError(handleApiError(error));
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loading message="Creating your course..." />;

    if (success) {
        return (
            <div className="min-h-screen bg-surface-muted flex items-center justify-center">
                <div className="max-w-md mx-auto text-center bg-surface rounded-2xl shadow-sm border border-line p-8">
                    <div className="w-20 h-20 bg-[var(--ok-soft)] rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-status-good" />
                    </div>
                    <h2 className="text-2xl font-bold text-ink mb-3">Course Created!</h2>
                    <p className="text-mutedink mb-6">Your course is ready. Taking you to your first practice session...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-muted">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                {/* Header */}
                <div className="bg-surface rounded-2xl shadow-sm border border-line p-8 mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/')}
                                className="p-2 hover:bg-surface-subtle rounded-lg transition-colors"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-ink">Create New Course</h1>
                                <p className="text-mutedink mt-1">Define your learning domains, or describe what you want and we'll draft the structure.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick generate */}
                <div className="bg-surface border border-line rounded-2xl p-6 mb-8">
                    <div className="flex items-center justify-between gap-6">
                        <div>
                            <h3 className="text-lg font-semibold text-ink">Generate from a description</h3>
                            <p className="text-mutedink text-sm mt-1">Skip the form — describe the course in plain English and we'll draft the structure for you to refine.</p>
                        </div>
                        <button
                            onClick={() => setShowQuickPrompt(true)}
                            className="flex-shrink-0 inline-flex items-center gap-2 bg-accent text-white px-6 py-3 rounded-lg font-medium hover:bg-accent-hover transition-colors"
                        >
                            Generate
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Course Details */}
                    <div className="bg-surface rounded-2xl shadow-sm border border-line p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <BookOpen className="w-6 h-6 text-read" />
                            <h2 className="text-2xl font-bold text-ink">Course Details</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-read mb-2">
                                    Course Name *
                                </label>
                                <input
                                    type="text"
                                    value={courseData.name}
                                    onChange={(e) => handleNameChange(e.target.value)}
                                    placeholder="e.g., Advanced React Development"
                                    className="w-full px-4 py-3 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-read mb-2">
                                    Course Code *
                                </label>
                                <input
                                    type="text"
                                    value={courseData.code}
                                    onChange={(e) => setCourseData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                    placeholder="e.g., REACT"
                                    className="w-full px-4 py-3 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                                    maxLength="10"
                                    required
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="block text-sm font-medium text-read mb-2">
                                Description
                            </label>
                            <textarea
                                value={courseData.description}
                                onChange={(e) => setCourseData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Describe what students will learn in this course..."
                                rows="3"
                                className="w-full px-4 py-3 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Domains */}
                    <div className="bg-surface rounded-2xl shadow-sm border border-line p-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <Target className="w-6 h-6 text-read" />
                                <h2 className="text-2xl font-bold text-ink">Learning Domains</h2>
                            </div>
                            <button
                                type="button"
                                onClick={addDomain}
                                className="flex items-center gap-2 px-4 py-2 bg-[var(--code-block-bg)] text-white rounded-lg hover:bg-[var(--code-block-bg)] transition-colors"
                            >
                                <Plus size={16} />
                                Add Domain
                            </button>
                        </div>

                        <div className="space-y-6">
                            {courseData.domains.map((domain, domainIndex) => (
                                <div key={domainIndex} className="border border-line rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-ink">
                                            Domain {domainIndex + 1}
                                        </h3>
                                        {courseData.domains.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDomain(domainIndex)}
                                                className="p-2 text-status-bad hover:bg-[var(--error-soft)] rounded-lg transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-read mb-2">
                                                Domain Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={domain.name}
                                                onChange={(e) => handleDomainNameChange(domainIndex, e.target.value)}
                                                placeholder="e.g., Hooks & State Management"
                                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-read mb-2">
                                                Key (auto-generated)
                                            </label>
                                            <input
                                                type="text"
                                                value={domain.key}
                                                onChange={(e) => updateDomain(domainIndex, 'key', e.target.value)}
                                                className="w-full px-3 py-2 border border-line-strong rounded-lg bg-surface-muted text-mutedink"
                                                placeholder="auto_generated"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-read mb-2">
                                                Weight (%)
                                            </label>
                                            <input
                                                type="number"
                                                value={domain.weight}
                                                onChange={(e) => updateDomain(domainIndex, 'weight', Number.parseInt(e.target.value, 10) || 0)}
                                                min="1"
                                                max="100"
                                                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-read mb-2">
                                            Description
                                        </label>
                                        <input
                                            type="text"
                                            value={domain.description}
                                            onChange={(e) => updateDomain(domainIndex, 'description', e.target.value)}
                                            placeholder="Brief description of this domain..."
                                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="flex items-start gap-3 p-3 border border-line rounded-lg cursor-pointer hover:bg-surface-muted">
                                            <input
                                                type="checkbox"
                                                checked={!!domain.sandbox_eligible}
                                                onChange={(e) => updateDomain(domainIndex, 'sandbox_eligible', e.target.checked)}
                                                className="mt-1 w-4 h-4 text-status-good rounded focus:ring-green-500"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 text-sm font-medium text-ink">
                                                    <Terminal size={14} className="text-status-good" />
                                                    Enable hands-on sandbox
                                                </div>
                                                <div className="text-xs text-mutedink mt-1">
                                                    Check only if this domain involves CLI, code, config, debugging, or troubleshooting practice.
                                                </div>
                                            </div>
                                        </label>
                                    </div>

                                    {/* Concepts */}
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <label className="block text-sm font-medium text-read">
                                                Concepts *
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => addConcept(domainIndex)}
                                                className="flex items-center gap-1 text-sm text-accent-ink hover:text-accent-ink"
                                            >
                                                <Plus size={14} />
                                                Add Concept
                                            </button>
                                        </div>

                                        <div className="space-y-2">
                                            {domain.concepts.map((concept, conceptIndex) => (
                                                <div key={conceptIndex} className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={concept}
                                                        onChange={(e) => updateConcept(domainIndex, conceptIndex, e.target.value)}
                                                        placeholder="e.g., useState, useEffect, custom hooks"
                                                        className="flex-1 px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                                                    />
                                                    {domain.concepts.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeConcept(domainIndex, conceptIndex)}
                                                            className="p-2 text-status-bad hover:bg-[var(--error-soft)] rounded-lg transition-colors"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Weight Summary */}
                        <div className="mt-6 p-4 bg-surface-muted rounded-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-read">Total Weight:</span>
                                <span className={`text-sm font-bold ${Math.abs(courseData.domains.reduce((sum, d) => sum + d.weight, 0) - 100) <= 1
                                        ? 'text-status-good'
                                        : 'text-status-bad'
                                    }`}>
                                    {courseData.domains.reduce((sum, d) => sum + d.weight, 0)}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="bg-[var(--error-soft)] border border-[var(--error-soft)] rounded-lg p-4 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-status-bad flex-shrink-0" />
                            <p className="text-status-bad">{error}</p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-8 py-4 bg-[var(--code-block-bg)] text-white rounded-xl font-semibold hover:bg-[var(--code-block-bg)] transition-colors disabled:opacity-50"
                        >
                            <Save size={20} />
                            Create Course
                        </button>
                    </div>
                </form>
            </div>

            {/* Quick generate modal */}
            {showQuickPrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-bold text-ink">Describe your course</h3>
                                <button
                                    onClick={() => setShowQuickPrompt(false)}
                                    className="p-2 hover:bg-surface-subtle rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-read mb-3">
                                    What do you want to learn?
                                </label>
                                <textarea
                                    value={quickPrompt}
                                    onChange={(e) => setQuickPrompt(e.target.value)}
                                    placeholder="Examples:

• React development from basics to advanced concepts
• AWS Solutions Architect certification prep
• Python for data science beginners
• Machine learning fundamentals
• Docker and Kubernetes for DevOps"
                                    rows="8"
                                    className="w-full px-4 py-3 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <button
                                    onClick={handleGenerateCourse}
                                    disabled={generating || !quickPrompt.trim()}
                                    className="flex items-center gap-2 bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {generating ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Generating...
                                        </>
                                    ) : (
                                        <>Generate course structure</>
                                    )}
                                </button>
                                <button
                                    onClick={() => setShowQuickPrompt(false)}
                                    className="px-6 py-3 text-read border border-line-strong rounded-lg font-medium hover:bg-surface-muted transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}